import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { toastStore, toast } from './toastStore'
import { useSyncExternalStore } from '../../hooks/useSyncExternalStore'
import styles from './Toast.module.css'

const ICONS = {
  success: 'check_circle',
  error: 'error_outline',
  info: 'info',
  alert: 'report_problem',
}

const POSITIONS = ['top-right', 'top-left', 'bottom-right', 'bottom-left']

function ToastItem({ id, variant, title, description, duration }) {
  const [isClosing, setIsClosing] = useState(false)
  const timerRef = useRef(null)
  const remainingRef = useRef(duration)
  const startRef = useRef(Date.now())

  const handleDismiss = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => toastStore.dismiss(id), 250)
  }, [id])

  useEffect(() => {
    if (!duration) return undefined
    startRef.current = Date.now()
    timerRef.current = setTimeout(handleDismiss, duration)
    return () => clearTimeout(timerRef.current)
  }, [duration, handleDismiss])

  const handleMouseEnter = () => {
    if (!duration) return
    clearTimeout(timerRef.current)
    remainingRef.current -= Date.now() - startRef.current
  }

  const handleMouseLeave = () => {
    if (!duration) return
    startRef.current = Date.now()
    timerRef.current = setTimeout(handleDismiss, remainingRef.current)
  }

  const classes = [styles.toast, styles[`toast--${variant}`], styles[isClosing ? 'toast--out' : 'toast--in']]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div className={styles.content}>
        <span className={styles.icon}>
          <span className="material-icons-outlined">{ICONS[variant]}</span>
        </span>
        <div className={styles.body}>
          <span className={styles.title}>{title}</span>
          {description && <span className={styles.desc}>{description}</span>}
        </div>
        <button type="button" className={styles.close} aria-label="Cerrar" onClick={handleDismiss}>
          <span className="material-icons-outlined">close</span>
        </button>
      </div>
      {duration ? <div className={styles.track} style={{ '--toast-duration': `${duration}ms` }} /> : null}
    </div>
  )
}

export function ToastProvider({ position = 'top-right' }) {
  const toasts = useSyncExternalStore(toastStore.subscribe, toastStore.getSnapshot, toastStore.getSnapshot)

  return createPortal(
    <div
      className={[styles.stack, styles[`stack--${position}`]].join(' ')}
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} {...t} />
      ))}
    </div>,
    document.body
  )
}

ToastProvider.propTypes = {
  position: PropTypes.oneOf(POSITIONS),
}

export { toast }
